import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, Button,
    alpha, Stack, Chip, Avatar, Divider,
    Fade, Grow, Paper,
    Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
    Assignment as HomeworkIcon,
    MenuBook as CurriculumIcon,
    LibraryBooks as MaterialIcon,
    VideoCall as MeetingIcon,
    DirectionsBus as TransportIcon,
    Payment as PaymentIcon,
    HowToReg as RegistrationIcon,
    EventNote as ExamIcon,
    Notifications as NotificationsIcon,
    CheckCircle as CheckCircleIcon,
    Phone as PhoneIcon,
    AutoAwesome as SparkleIcon,
    School as StudentIcon,
    FamilyRestroom as ParentIcon,
    Person as TeacherIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';

const School = () => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    // ========== HERO ==========
    const heroContent = {
        badge: 'MASH MATRICULATION SCHOOL',
        title: 'MASH MATRICULATION SCHOOL',
        titleHighlight: 'Every School Need.',
        subtitle:
            'Registration, Homework, Materials, Curriculum, Meetings, Transport, Payments etx. — all in one powerful platform for students, parents, and teachers.',
    };

    const heroHighlights = [
        {
            title: 'School Announcements',
            desc: 'Circulars, holidays and events reach every parent instantly.',
            icon: <NotificationsIcon />, bg: '#e7f1ff', color: '#0d6efd',
        },
        {
            title: 'Online Classes',
            desc: 'Join live sessions and revisit recorded lessons anytime.',
            icon: <MeetingIcon />, bg: '#f8d7da', color: '#dc3545',
        },
        {
            title: 'Daily Updates',
            desc: 'Homework, exam schedules and results in one place.',
            icon: <CheckCircleIcon />, bg: '#d1e7dd', color: '#198754',
        },
    ];

    // ========== MODULES ==========
    const modules = [
        { title: 'Registration', desc: 'Apply for admission online, upload documents and get instant enrollment confirmation.', icon: <RegistrationIcon />, color: '#0d6efd' },
        { title: 'Homework', desc: 'Teachers assign work, students submit online, and marks are shared with parents.', icon: <HomeworkIcon />, color: '#198754' },
        { title: 'Study Material', desc: 'Subject-wise notes, PDFs and recorded video lectures available round the clock.', icon: <MaterialIcon />, color: '#6f42c1' },
        { title: 'Curriculum', desc: 'Structured LKG to Class 12 syllabus with chapter-wise lesson plans.', icon: <CurriculumIcon />, color: '#fd7e14' },
        { title: 'Meetings', desc: 'Book parent-teacher meetings and attend live online classes from any device.', icon: <MeetingIcon />, color: '#dc3545' },
        { title: 'Transport', desc: 'Live GPS bus tracking with route, pickup time and delay alerts.', icon: <TransportIcon />, color: '#20c997' },
        { title: 'Payments', desc: 'Pay tuition, bus and exam fees online and download receipts in one click.', icon: <PaymentIcon />, color: '#ffc107' },
        { title: 'Exams', desc: 'Exam timetables, online tests, report cards and progress tracking.', icon: <ExamIcon />, color: '#0dcaf0' },
    ];

    // ========== WHY ==========
    const whyPoints = [
        'One login for students, parents and teachers',
        'Works on mobile, tablet and desktop',
        'Instant notifications for homework, exams and fees',
        'Paperless admission and digital fee receipts',
        'Secure data with role-based access',
        'Regular updates and dedicated support',
    ];

    // ========== ROLES ==========
    const roles = [
        {
            title: 'For Students',
            icon: <StudentIcon />,
            color: '#0d6efd',
            points: [
                'View and submit homework online',
                'Download notes, PDFs and videos',
                'Attend live classes',
                'Take online tests and see results',
                'Check daily timetable',
            ],
        },
        {
            title: 'For Parents',
            icon: <ParentIcon />,
            color: '#198754',
            points: [
                'Track attendance and homework status',
                'Pay fees online with instant receipts',
                'Follow the school bus live on the map',
                'Book parent-teacher meetings',
                'Receive school circulars and alerts',
            ],
        },
        {
            title: 'For Teachers',
            icon: <TeacherIcon />,
            color: '#fd7e14',
            points: [
                'Assign and grade homework quickly',
                'Upload study materials and lesson plans',
                'Conduct live online classes',
                'Publish exam schedules and marks',
                'Send announcements to a class or the whole school',
            ],
        },
    ];

    // ========== HOW IT WORKS ==========
    const steps = [
        { title: 'Register', desc: 'Fill the online admission form and upload the required documents.' },
        { title: 'Get your login', desc: 'Receive separate login details for the student and the parent.' },
        { title: 'Learn and track', desc: 'Access classes, homework, materials and exams from the dashboard.' },
        { title: 'Stay connected', desc: 'Pay fees, track the bus and talk to teachers without visiting the school.' },
    ];

    // ========== FAQ ==========
    const faqs = [
        {
            q: 'Who can use the Na Mash school portal?',
            a: 'Students, parents and teachers each get their own login with the features they need.',
        },
        {
            q: 'How do I apply for admission?',
            a: 'Use the New Admission button on this page, fill in the form, upload the documents, and the school office will confirm your enrollment.',
        },
        {
            q: 'Can I pay school fees online?',
            a: 'Yes. Tuition, transport and exam fees can be paid online, and a receipt is available to download immediately.',
        },
        {
            q: 'Can parents track the school bus?',
            a: 'Yes. The portal shows the live location of the bus on its route and sends alerts about delays.',
        },
        {
            q: 'Does it work on mobile phones?',
            a: 'The portal works on phones, tablets and computers, so classes and updates are always within reach.',
        },
    ];

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#ffffff' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 14, md: 18 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '5%', right: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)', animation: 'pulse 4s ease-in-out infinite' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)', animation: 'pulse 6s ease-in-out infinite' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1,mt:2 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Fade in timeout={800}>
                                <Box>

                                    {/* ==================== LOGO + TITLE (SAME ROW) ==================== */}
                                    <Stack
                                        direction="row"
                                        spacing={{ xs: 2, md: 3 }}
                                        alignItems="center"
                                        sx={{ mb: 3 }}
                                    >
                                        <Box
                                            component="img"
                                            src="/images/mash.png"
                                            alt="MASH Matriculation School Logo"
                                            sx={{
                                                height: { xs: 60, md: 90 },
                                                width: 'auto',
                                                flexShrink: 0,
                                                objectFit: 'contain',
                                                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                                            }}
                                        />

                                        <Typography
                                            variant="h1"
                                            sx={{
                                                fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem', lg: '3.6rem' },
                                                fontWeight: 800,
                                                lineHeight: 1.15,
                                                color: 'white',
                                                m: 0,
                                            }}
                                        >
                                            {heroContent.title}
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mb: 4,
                                            color: 'rgba(255,255,255,0.95)',
                                            maxWidth: 600,
                                            fontWeight: 400,
                                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                                        }}
                                    >
                                        {heroContent.subtitle}
                                    </Typography>

                                    {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Button
                                            size="large"
                                            startIcon={<RegistrationIcon />}
                                            sx={{
                                                py: 1.4, px: 3,
                                                bgcolor: 'white',
                                                color: '#1d748b',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                '&:hover': { bgcolor: '#f8f9fa' },
                                            }}
                                        >
                                            New Admission
                                        </Button>
                                        <Button
                                            size="large"
                                            variant="outlined"
                                            startIcon={<PhoneIcon />}
                                            sx={{
                                                py: 1.4, px: 3,
                                                borderColor: 'white',
                                                color: 'white',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                            }}
                                        >
                                            Contact Office
                                        </Button>
                                    </Stack> */}
                                </Box>
                            </Fade>
                        </Grid>

                        {/* Right side — highlights card */}
                        <Grid item xs={12} md={5}>
                            <Grow in timeout={1000}>
                                <Paper elevation={24} sx={{ p: 3, borderRadius: 4, bgcolor: 'white' }}>
                                    <Box sx={{ mb: 2 }}>
                                        {/* <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            Life at Mash, made simple
                                        </Typography> */}
                                        <Typography variant="caption" color="text.secondary">
                                            What the portal gives you every day
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    <Stack spacing={2}>
                                        {heroHighlights.map((h) => (
                                            <Stack key={h.title} direction="row" spacing={2} alignItems="center">
                                                <Avatar sx={{ bgcolor: h.bg, color: h.color, width: 40, height: 40 }}>
                                                    {h.icon}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {h.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {h.desc}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grow>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ==================== MODULE GRID ==================== */}
            <Container maxWidth="xl" sx={{ mt: -8, mb: 8, position: 'relative', zIndex: 2 }}>
                <Grid container spacing={3}>
                    {modules.map((m, i) => (
                        <Grid item xs={12} sm={6} md={3} key={m.title}>
                            <Grow in timeout={(i + 1) * 100}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                                        bgcolor: 'white',
                                        height: '100%',
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 50px ${alpha(m.color, 0.25)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2,
                                            bgcolor: alpha(m.color, 0.12),
                                            color: m.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        {m.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {m.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {m.desc}
                                    </Typography>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== WHY THIS PORTAL ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
                            A smarter way to run a school day
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
                            The Na Mash portal brings admissions, learning, communication and payments together, so parents spend less time on paperwork and teachers spend more time teaching.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.06)' }}>
                            <Stack spacing={2}>
                                {whyPoints.map((p) => (
                                    <Stack key={p} direction="row" spacing={1.5} alignItems="center">
                                        <CheckCircleIcon sx={{ color: '#52d370' }} />
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{p}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* ==================== ROLES ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 8 }}>
                <Container maxWidth="xl">
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, textAlign: 'center', fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
                        Built for everyone in the school
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 5, textAlign: 'center' }}>
                        Each login shows exactly what that person needs.
                    </Typography>

                    <Grid container spacing={4}>
                        {roles.map((r) => (
                            <Grid item xs={12} md={4} key={r.title}>
                                <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
                                    <Avatar sx={{ bgcolor: alpha(r.color, 0.12), color: r.color, width: 56, height: 56, mb: 2 }}>
                                        {r.icon}
                                    </Avatar>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                        {r.title}
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {r.points.map((p) => (
                                            <Stack key={p} direction="row" spacing={1.5} alignItems="flex-start">
                                                <CheckCircleIcon sx={{ color: r.color, fontSize: 20, mt: '2px' }} />
                                                <Typography variant="body2">{p}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== HOW IT WORKS ==================== */}
            <Container maxWidth="xl" sx={{ py: 8 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 5, textAlign: 'center', fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
                    Get started in four steps
                </Typography>
                <Grid container spacing={4}>
                    {steps.map((s, i) => (
                        <Grid item xs={12} sm={6} md={3} key={s.title}>
                            <Box sx={{ textAlign: 'center', px: 2 }}>
                                <Avatar
                                    sx={{
                                        width: 60, height: 60, mx: 'auto', mb: 2,
                                        background: BRAND_GRADIENT, fontWeight: 800, fontSize: '1.4rem',
                                    }}
                                >
                                    {i + 1}
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{s.title}</Typography>
                                <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== FAQ ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 8 }}>
                <Container maxWidth="md">
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 4, textAlign: 'center', fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
                        Frequently asked questions
                    </Typography>
                    {faqs.map((f) => (
                        <Accordion key={f.q} disableGutters elevation={0} sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid rgba(0,0,0,0.08)', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ fontWeight: 600 }}>{f.q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary">{f.a}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Container>
            </Box>

            <HomeFooter />

            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.8; }
                }
            `}</style>
        </Box>
    );
};

export default School;